import { c as _c } from "react/compiler-runtime";
import { Badge } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { AddSectionButton, AddSectionOverlay, toneStyles } from '@/components/block-primitives';
import type { RecordBlockConfig, RecordField } from './artifact-block-types';
import EditableText from './editable-text';
const initials = (name: string) => name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
// Array.from splits by code point, so an emoji/astral first char isn't cut mid-surrogate.
.map(word => Array.from(word)[0]).join('').toUpperCase();
type ArtifactRecordBlockProps = {
  config: RecordBlockConfig;
  editable?: boolean;
  onChange?: (next: RecordBlockConfig) => void;
};
const ArtifactRecordBlock = t0 => {
  const $ = _c(60);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [addNote, setAddNote] = useState(false);
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  let t3;
  if ($[3] !== config.fields || $[4] !== patch) {
    t3 = (index, next_0) => patch({
      fields: config.fields.map((field, i) => i === index ? {
        ...field,
        ...next_0
      } : field)
    });
    $[3] = config.fields;
    $[4] = patch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const patchField = t3;
  const showNote = !!config.note || addNote;
  const hasSubtitle = !!config.subtitle;
  const hasSubtitleRow = editable || hasSubtitle;
  let t4;
  if ($[6] !== editable || $[7] !== showNote) {
    t4 = editable && !showNote && <AddSectionOverlay position="bottom"><AddSectionButton label="Add a note" onClick={() => setAddNote(true)} /></AddSectionOverlay>;
    $[6] = editable;
    $[7] = showNote;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = config.name;
  const t6 = hasSubtitleRow ? "items-start" : "items-center";
  let t7;
  if ($[9] !== t6) {
    t7 = cx("flex gap-3 px-4 py-4 md:gap-4 md:px-6 md:py-5", t6);
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== config.monogram || $[12] !== config.name) {
    t8 = config.monogram ?? initials(config.name);
    $[11] = config.monogram;
    $[12] = config.name;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary text-label-1 text-primary">{t8}</div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== patch) {
    t10 = name => patch({
      name
    });
    $[16] = patch;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== config.name || $[19] !== editable || $[20] !== t10) {
    t11 = <EditableText as="div" block={true} className="break-words text-label-1 text-primary" editable={editable} label="Record name" onSubmit={t10} value={config.name} />;
    $[18] = config.name;
    $[19] = editable;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== config.subtitle || $[23] !== editable || $[24] !== hasSubtitle || $[25] !== patch) {
    t12 = (editable || hasSubtitle) && <EditableText as="div" block={true} className="break-words text-caption-4 text-tertiary" editable={editable} label="Record subtitle" onSubmit={subtitle => patch({
      subtitle
    })} optional={true} placeholder="Add a subtitle" value={config.subtitle ?? ""} />;
    $[22] = config.subtitle;
    $[23] = editable;
    $[24] = hasSubtitle;
    $[25] = patch;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t11}{t12}</div>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== config.badge) {
    t14 = config.badge && <Badge className="shrink-0" variant={toneStyles[config.badge.tone].badgeVariant}>{config.badge.label}</Badge>;
    $[30] = config.badge;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14 || $[34] !== t7 || $[35] !== t9) {
    t15 = <div className={t7}>{t9}{t13}{t14}</div>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t7;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== config.fields || $[38] !== editable || $[39] !== patchField) {
    let t17;
    if ($[41] !== editable || $[42] !== patchField) {
      t17 = (field_0, index_0) => <div className="grid grid-cols-1 gap-0.5 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-baseline md:gap-4 border-b border-tertiary py-2.5 last:border-b-0" key={index_0}><EditableText as="dt" className="max-md:break-words text-body-2 text-secondary" editable={editable} label="Field label" onSubmit={label => patchField(index_0, {
          label
        })} value={field_0.label} /><dd className="min-w-0"><EditableText className={cx("min-w-0 break-words text-label-2", field_0.tone ? toneStyles[field_0.tone].value : "text-primary")} editable={editable} label="Field value" onSubmit={value => patchField(index_0, {
            value
          })} value={field_0.value} /></dd></div>;
      $[41] = editable;
      $[42] = patchField;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    t16 = config.fields.map(t17);
    $[37] = config.fields;
    $[38] = editable;
    $[39] = patchField;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[44] !== t16) {
    t17 = <dl className="border-t border-tertiary px-4 md:px-6">{t16}</dl>;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== addNote || $[47] !== config.note || $[48] !== editable || $[49] !== patch || $[50] !== showNote) {
    t18 = showNote && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><EditableText autoFocus={addNote} block={true} editable={editable} label="Record note" onSubmit={note => patch({
        note
      })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} /></div>;
    $[46] = addNote;
    $[47] = config.note;
    $[48] = editable;
    $[49] = patch;
    $[50] = showNote;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== config.name || $[53] !== t15 || $[54] !== t17 || $[55] !== t18) {
    t19 = <section aria-label={t5} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t15}{t17}{t18}</section>;
    $[52] = config.name;
    $[53] = t15;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t19 || $[58] !== t4) {
    t20 = <div className="group relative">{t4}{t19}</div>;
    $[57] = t19;
    $[58] = t4;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  return t20;
};
export default ArtifactRecordBlock;